import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Table, Button, Select, Popconfirm, Tooltip } from "antd";
import styles from "./styles/agency.scss";
import {
  getAgencies,
  selectAll,
  updateAgency,
  createAgency,
  deleteAgency,
  generateFeedSecret,
  revokeFeedSecret
} from "./agency.ducks";
import { EditableFormRow } from "./EditableTable/EditableFormRow";
import EditableCell from "./EditableTable/EditableCell";
import AgencyPopup from "./AgencyPopup";
import AgencyFeedModal from "./AgencyFeedModal";
import AgencySearchForm from "./AgencySearchForm";
import { IndexPagination } from "../../../components/IndexPagination";
import { AgencySyncStatus, ImportMappingType } from "../../../models/enums";
import PageTitle from "../PageTitle";
import { formatDate, notification } from "../../../services";
import history from "../../../history";

const NUMERIC_CRITERIA = ["syncStatus", "staleHours"];

// the dashboard links here with preset filters, e.g. ?isActive=true&staleHours=24
const readCriteriaFromSearch = (search) => {
  const criteria = {};
  const params = new URLSearchParams(search || "");

  params.forEach((value, key) => {
    criteria[key] = NUMERIC_CRITERIA.indexOf(key) >= 0 ? Number(value) : value;
  });

  return criteria;
};

const writeCriteriaToSearch = (criteria) => {
  const params = new URLSearchParams();

  Object.keys(criteria || {}).forEach((key) => {
    const value = criteria[key];

    if (value !== null && value !== undefined && value !== "") {
      params.set(key, value);
    }
  });

  history.replace({ search: params.toString() });
};

const tableComponents = {
  body: {
    row: EditableFormRow,
    cell: EditableCell
  }
};

const ManageAgencyPage = props => {
  const {
    agencies,
    page,
    itemPerPage,
    total,
    getAgencies,
    updateAgency,
    deleteAgency,
    generateFeedSecret,
    revokeFeedSecret
  } = props;

  const [loading, setLoading] = useState(false);
  const [createPopupVisible, setCreatePopupVisible] = useState(false);
  const [initialCriteria] = useState(() =>
    readCriteriaFromSearch(props.location && props.location.search ? props.location.search : window.location.search)
  );
  const [feedModalAgencyId, setFeedModalAgencyId] = useState(null);

  const currentPage = useRef(1);
  const searchCriteria = useRef(initialCriteria);
  const columns = useRef([]);

  const deleteTooltip = <span>{"Obriši agenciju"}</span>
  const deactivateTooltip = <span>{"Deaktiviraj agenciju"}</span>
  const activateTooltip = <span>{"Aktiviraj agenciju"}</span>
  const feedTooltip = <span>{"Feed aktivnih oglasa"}</span>

  useEffect(() => {
    const { page, itemPerPage } = props;

    setLoading(true);
    getAgencies(initialCriteria, page, itemPerPage, () => setLoading(false));

    createColumns();
  }, []);

  const createColumns = () => {
    const columnDefinition = [
      {
        title: "Korisničko ime",
        dataIndex: "username",
        key: "username",
        width: "150px"
      },
      {
        title: "E-mail",
        dataIndex: "email",
        key: "email",
        width: "150px"
      },
      {
        title: "XML Format",
        dataIndex: "mapperType",
        key: "mapperType",
        render: (text, record) => (
          <Select
            placeholder={"XML format"}
            value={record.mapperType}
            onChange={value => handleFormatChange(record, value)}
          >
            {ImportMappingType.map(format => (
              <Select.Option key={format.value} value={format.value}>
                {format.name}
              </Select.Option>
            ))}
          </Select>
        )
      },
      {
        title: "URL",
        dataIndex: "sourceUrl",
        key: "sourceUrl",
        editable: true,
        width: "30%"
      },
      {
        title: "Status",
        dataIndex: "isActive",
        key: "isActive",
        width: "105px",
        render: (text, record) => (
          record.isActive ? "Aktivan" : "Neaktivan"
        )
      },
      {
        title: "Status sinkronizacije",
        dataIndex: "syncStatus ",
        key: "syncStatus ",
        width: "115px",
        render: (text, record) => {
          if (record.syncStatus === AgencySyncStatus.failed) {
            return <span style={{ color: "red" }}>{"Neuspješno"}</span>;
          } else if (record.syncStatus === AgencySyncStatus.success) {
            return <span style={{ color: "green" }}>{"Uspješno"}</span>;
          } else if (record.syncStatus === AgencySyncStatus.pending) {
            return <span>{"Na čekanju"}</span>;
          } else {
            return <span></span>;
          }
        }
      },
      {
        title: "Datum zadnje uspješne sinkronizacije",
        dataIndex: "syncDate",
        key: "syncDate",
        width: "110px",
        render: (text, record) => text ? formatDate(text) : ""
      },
      {
        title: "Akcije",
        key: "block",
        align: "center",
        dataIndex: "id",
        fixed: "right",
        width: 140,
        render: (value, record) => (
          <div className={styles.actions}>
            <Tooltip placement="topLeft" title={feedTooltip}>
              <Button
                type={record.feedSecret ? "primary" : "default"}
                icon="link"
                onClick={() => setFeedModalAgencyId(record.id)}
              />
            </Tooltip>

            <Tooltip placement="topLeft" title={deleteTooltip}>
              <Popconfirm
                title={"Sigurno izbrisati?"}
                onConfirm={() => handleDelete(record.id)}
                okText={"Da"}
                cancelText={"Ne"}
              >
                <Button type="primary" icon="delete" />
              </Popconfirm>
            </Tooltip>

            {record.isActive ? (
              <Tooltip placement="topLeft" title={deactivateTooltip}>
                <Popconfirm
                  title={"Sigurno deaktivirati?"}
                  onConfirm={() => handleAgencyUpdate({ ...record, isActive: false })}
                  okText={"Da"}
                  cancelText={"Ne"}
                >
                  <Button type="primary" icon="pause-circle" />
                </Popconfirm>
              </Tooltip>
            ) : (
              <Tooltip placement="topLeft" title={activateTooltip}>
                <Popconfirm
                  title={"Sigurno aktivirati?"}
                  onConfirm={() => handleAgencyUpdate({ ...record, isActive: true })}
                  okText={"Da"}
                  cancelText={"Ne"}
                >
                  <Button type="default" icon="play-circle" />
                </Popconfirm>
              </Tooltip>
            )}
          </div>
        )
      },
    ];

    columns.current = columnDefinition.map(col => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: record => ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave: handleAgencyUpdate
        })
      };
    });
  };

  const handleCreatePopupOpen = () => {
    setCreatePopupVisible(true);
  };

  const handleEditPopupOpen = () => {
    setCreatePopupVisible(true);
  };

  const handlePopupClose = () => {
    setCreatePopupVisible(false);
  };

  const handleAgencyCreate = (value, onSubmit, callback) => {
    setCreatePopupVisible(true);
    onSubmit(value, status => {
      if (status) {
        setCreatePopupVisible(false);
        callback && callback();
        search();
      }
    });
  };

  const handleAgencyUpdate = row => {
    setLoading(true);
    updateAgency(row.id, row, () => {
      search(searchCriteria.current, currentPage.current);
    });
  };

  const handleFormatChange = (record, value) => {
    const data = { ...record, mapperType: value };
    handleAgencyUpdate(data);
  };

  const handlePageChange = page => {
    currentPage.current = page;
    search(searchCriteria.current, page);
  };

  const handleSearch = criteria => {
    searchCriteria.current = criteria;
    writeCriteriaToSearch(criteria);
    search(searchCriteria.current);
  };

  const search = (searchCriteria = {}, page = 1) => {
    setLoading(true);
    getAgencies(searchCriteria, page, itemPerPage, () => setLoading(false));
  };

  const handleDelete = id => {
    setLoading(true);
    deleteAgency(id, () => search(searchCriteria.current));
  };

  const handleGenerateFeedSecret = record => {
    setLoading(true);
    generateFeedSecret(record.id, success => {
      if (success) {
        notification.show("Ključ je generiran");
      } else {
        notification.showError("Generiranje ključa nije uspjelo");
      }
      search(searchCriteria.current, currentPage.current);
    });
  };

  const handleRevokeFeedSecret = record => {
    setLoading(true);
    revokeFeedSecret(record.id, success => {
      if (success) {
        notification.show("Ključ je opozvan");
      } else {
        notification.showError("Opoziv ključa nije uspio");
      }
      search(searchCriteria.current, currentPage.current);
    });
  };

  // Derived from the list so the modal reflects the refreshed agency after generate/revoke
  const feedModalAgency = agencies.find(agency => agency.id === feedModalAgencyId);

  return (
    <React.Fragment>
      <PageTitle title={"Agencije"} />
      <div className={styles.form}>
        <Button onClick={handleCreatePopupOpen} className={styles.btnCreate}>
          {"Dodaj agenciju"}
        </Button>
        <AgencySearchForm onSubmit={handleSearch} className={""} initialValues={initialCriteria} />
      </div>
      <Table
        loading={loading}
        components={tableComponents}
        rowKey="id"
        bordered
        dataSource={agencies}
        columns={columns.current}
        pagination={false}
        className="admin-table"
      />
      <IndexPagination
        total={total}
        page={page}
        itemPerPage={itemPerPage}
        onPageChange={handlePageChange}
      />
      <AgencyPopup
        visible={createPopupVisible}
        onCancel={handlePopupClose}
        onCreate={handleAgencyCreate}
      />
      <AgencyFeedModal
        agency={feedModalAgency}
        visible={!!feedModalAgency}
        loading={loading}
        onClose={() => setFeedModalAgencyId(null)}
        onGenerate={handleGenerateFeedSecret}
        onRevoke={handleRevokeFeedSecret}
      />
    </React.Fragment>
  );
};

ManageAgencyPage.defaultProps = {
  agencies: []
};

export default connect(
  state => ({
    ...selectAll(state)
  }),
  {
    getAgencies,
    updateAgency,
    createAgency,
    deleteAgency,
    generateFeedSecret,
    revokeFeedSecret
  }
)(ManageAgencyPage);

export { default as reducer } from "./agency.ducks";



// WEBPACK FOOTER //
// ./src/routes/Admin/ManageAgencyPage/index.tsx